import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { Box, Icon, Dropdown, ClickableContent, Text, Button } from '@deseretbook/react-ui'
import useDropdown from '../../../hooks/useDropdown'
import './MediaCard.css'
import { detailsUrl } from '../../../utils/routes'
import PlayerWrapper from './PlayerWrapper'
import useUserSub from '../../../hooks/useUserSub'
import CopyTooltip from '../../tooltips/CopyTooltip'
import { truncateText } from '../../../utils/truncateText'

/**
 * A component for displaying a media object.
 *
 * TODO write tests for this.
 * TODO Add story for this
 * @param {object} props - component props
 * @returns {function} Component
 */
function MediaCard(props) {
  const {
    className,
    media,
    hideOptions,
    url,
    mediaType,
    isPlus,
    isVip,
    variantSku,
    isDiscover,
    ...rest
  } = props

  // Set media types
  const isEbook = (media && media.ebook) || mediaType === 'ebook'
  const isAudiobook = (media && media.audiobook) || mediaType === 'audiobook'

  const dropdown = useDropdown()
  const { userVip } = useUserSub()

  if (!media) {
    return false
  }
  const classes = classNames('MediaCard', className)
  const titleClasses = classNames('MediaCardTitle', className)
  const linkClasses = classNames('MediaCardLink', className)
  const linkContainerClasses = classNames('MediaCardLinkContainer', className)
  const sku = (media[mediaType] && media[mediaType].sku) || variantSku
  const landingPage = detailsUrl(media.mediaId, sku)
  const cover = (media && media[mediaType] && media[mediaType].cover) || media.cover

  const { inLibrary = false, subscribed = false } =
    (media[mediaType] && media[mediaType].userInfo) || {}

  const figure = (
    <PlayerWrapper
      isAudiobook={media && media.audiobook}
      inLibrary={inLibrary || subscribed}
      isDiscover={isDiscover}
      media={media}
      className={classes}
      landingPage={landingPage}
      style={{ cursor: 'pointer', marginBottom: '0.5rem', borderRadius: '1rem' }}
    >
      <figure className="image">
        <img src={cover.listingUrl} alt={media.title} />
      </figure>
    </PlayerWrapper>
  )

  if (hideOptions) {
    return figure
  }

  const titleInfo = (
    <Box isColumn>
      <Text
        title={media.title}
        style={{
          fontSize: '1rem',
          lineHeight: '18.75px',
          fontWeight: 700,
          fontFamily: 'Roboto, sans-serif',
          marginBottom: '0.25rem',
          height: '40px',
        }}
      >
        {truncateText(media.title, 28)}
      </Text>
    </Box>
  )

  const iconStyles = {
    borderRadius: '1rem',
    backgroundColor: '#B6BFBF26',
    padding: '0.25rem',
    width: '1.5rem',
    height: '1.5rem',
    marginRight: '0.5rem',
  }

  const dbcomUrl = (media && media[mediaType] && media[mediaType].productUrl) || ''

  /** TODO add an `Image` component and change this instance of it. */
  return (
    <>
      <div style={{ marginBottom: '1.5rem', width: '200px' }}>
        <Box alignItems="center" justifyContent="start" style={{ marginBottom: '0.5rem' }}>
          {isEbook && <Icon name="ebook" style={iconStyles} />}
          {isAudiobook && <Icon name="audiobook" style={iconStyles} />}
          {isPlus && !userVip && <Icon name="bookshelfPlusNew" style={{ width: '84px' }} />}
          {(isPlus || isVip) && userVip && (
            <Icon
              name="digitalVipNew"
              style={{
                width: '123px',
              }}
            />
          )}
        </Box>
        {figure}
        <Box alignItems="start" justifyContent="space-between">
          <PlayerWrapper
            isAudiobook={media && media.audiobook}
            inLibrary={inLibrary || subscribed}
            media={media}
            className={titleClasses}
            style={{ cursor: 'pointer' }}
            landingPage={landingPage}
            isDiscover={isDiscover}
          >
            {titleInfo}
          </PlayerWrapper>
          <ClickableContent isPaddingless>
            <div {...dropdown.triggerProps}>
              <Icon name="options" />
            </div>
          </ClickableContent>
        </Box>
      </div>
      <Dropdown {...dropdown.dropdownProps} id="MediaCardDropdown">
        <Box isColumn alignItems="start" className={linkContainerClasses}>
          <Link to={landingPage} {...rest} className={linkClasses}>
            <Icon name="info" className="link-icon" />
            <Text>Title Details</Text>
          </Link>
          <CopyTooltip textToCopy={dbcomUrl}>
            <Button {...rest} className={linkClasses} style={{ border: 'none' }}>
              <Icon name="share" className="link-icon" />
              <Text>Share Title</Text>
            </Button>
          </CopyTooltip>
        </Box>
      </Dropdown>
    </>
  )
}

MediaCard.defaultProps = {
  className: '',
  isAudiobook: false,
  hideOptions: false,
  url: '',
  mediaType: 'ebook',
  isDiscover: false,
}

MediaCard.propTypes = {
  /** Additional classes */
  className: PropTypes.string,
  /** Media object to be rendered */
  media: PropTypes.shape({
    mediaId: PropTypes.number,
    bookId: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    authors: PropTypes.shape([]),
    cover: PropTypes.shape({
      listingUrl: PropTypes.string,
    }),
    topics: PropTypes.shape([]),
    categories: PropTypes.shape([]),
    ebook: PropTypes.shape({
      sku: PropTypes.string,
    }),
    audiobook: PropTypes.shape({
      sku: PropTypes.string,
    }),
  }).isRequired,
  hideOptions: PropTypes.bool,
  /** Link override */
  url: PropTypes.string,
  mediaType: PropTypes.string,
  isDiscover: PropTypes.bool,
}

export default MediaCard
